import {
  NormalizeRfiStatus,
  PushpinAttributesType,
  RfiCategory,
  RfiCostImpact,
  RfiDiscipline,
  RfiPriority,
  RfiScheduleImpact,
  RfiStatus,
} from './enums';
import { OxygenId } from './system';

export interface NormalizeRfi {
  id: string;
  customIdentifier: string;
  title: string;
  status: NormalizeRfiStatus;
  assignedTo: string;
  virtualFolderUrn?: string;
  dueDate?: string;
  location: string;
  priority?: RfiPriority;
  createdBy: string;
  closedAt?: string;
  createdAt: string;
}

/**
 *
 * @export
 * @interface BaseRfi
 */
export interface BaseRfi {
  /**
   * The ID of the RFI.
   * @type {string}
   * @memberof Rfi
   */
  id: string;
  /**
   * Identifier of the RFI given by user.
   * @type {string}
   * @memberof Rfi
   */
  customIdentifier?: string;
  /**
   * The name of the RFI.
   * @type {string}
   * @memberof Rfi
   */
  title: string;
  /**
   * The question of the RFI.
   * @type {string}
   * @memberof Rfi
   */
  question?: string;
  /**
   *
   * @type {RfiStatus}
   * @memberof Rfi
   */
  status: RfiStatus;
  /**
   *
   * @type {OxygenId}
   * @memberof Rfi
   */
  assignedTo: OxygenId;
  /**
   *
   * @type {OxygenId}
   * @memberof Rfi
   */
  managerId?: OxygenId;
  /**
   *
   * @type {OxygenId}
   * @memberof Rfi
   */
  constructionManagerId?: OxygenId;
  /**
   *
   * @type {OxygenId}
   * @memberof Rfi
   */
  architectId?: OxygenId;
  /**
   *
   * @type {OxygenId}
   * @memberof Rfi
   */
  reviewerId?: OxygenId;
  /**
   * The type of assignee the RFI is assigned to.
   * @type {string}
   * @memberof Rfi
   */
  assignedToType?: AssignedToType;
  /**
   *
   * @type {RfiLocation}
   * @memberof Rfi
   */
  location?: RfiLocation;
  /**
   * The document urn related to the RFI. Identifies whether this RFI is a pushpin RFI or a project-related RFI. A pushpin is a visual marker that denotes the location of an RFI in a document. A project-related RFI is assigned a null value, and a pushpin RFI is assigned the item ID of the document associated with the pushpin.
   * @type {string}
   * @memberof Rfi
   */
  linkedDocument?: string;
  /**
   * The document version when the RFI attached to it
   * @type {number}
   * @memberof Rfi
   */
  linkedDocumentVersion?: number;
  /**
   * The document version when the RFI, that attached to it, was closed
   * @type {number}
   * @memberof Rfi
   */
  linkedDocumentCloseVersion?: number; // BIM only
  /**
   * The number of the rfi attachments
   * @type {number}
   * @memberof Rfi
   */
  attachmentsCount?: number;
  /**
   * The number of the rfi comments
   * @type {number}
   * @memberof Rfi
   */
  commentsCount?: number;
  /**
   * An official response for the RFI.
   * @type {string}
   * @memberof Rfi
   */
  officialResponse?: string;
  /**
   * The timestamp of the when the RFI was responded, in the following format: YYYY-MM-DDThh:mm:ss.sz.
   * @type {Date}
   * @memberof Rfi
   */
  respondedAt?: Date;
  /**
   * The Autodesk id of the user who responded the RFI
   * @type {string}
   * @memberof Rfi
   */
  respondedBy?: string;
  /**
   * The Autodesk ID of the user who created the RFI.
   * @type {string}
   * @memberof Rfi
   */
  createdBy: string;
  /**
   * The timestamp of the date and time the RFI was created, in the following format: YYYY-MM-DDThh:mm:ss.sz.
   * @type {Date}
   * @memberof Rfi
   */
  createdAt: string;
  /**
   * The Autodesk ID of the user who updated the RFI.
   * @type {string}
   * @memberof Rfi
   */
  updatedBy?: string;
  /**
   * The timestamp of the date and time the RFI was updated, in the following format: YYYY-MM-DDThh:mm:ss.sz.
   * @type {Date}
   * @memberof Rfi
   */
  updatedAt?: Date;
  /**
   * The timestamp of the date and time the RFI was closed, in the following format: YYYY-MM-DDThh:mm:ss.sz.
   * @type {Date}
   * @memberof Rfi
   */
  closedAt: string;
  /**
   * The Autodesk ID of the user who closed the RFI.
   * @type {string}
   * @memberof Rfi
   */
  closedBy?: string;
  /**
   *
   * @type {string}
   * @memberof Rfi
   */
  containerId?: string;
  /**
   * The suggested answer for the RFI.
   * @type {string}
   * @memberof Rfi
   */
  suggestedAnswer?: string;
  /**
   * A list of alternative reviewers.
   * @type {Array<string>}
   * @memberof Rfi
   */
  coReviewers?: Array<string>;
  /**
   * The list of users who will be notified of changes to the RFI.
   * @type {Array<OxygenId>}
   * @memberof Rfi
   */
  distributionList?: Array<OxygenId>;
  /**
   * The timestamp of the date and time the RFI was answered, in the following format: YYYY-MM-DDThh:mm:ss.sz.
   * @type {Date}
   * @memberof Rfi
   */
  answeredAt?: Date;
  /**
   * The Autodesk ID of the user who answered the RFI.
   * @type {string}
   * @memberof Rfi
   */
  answeredBy?: string;
  /**
   *
   * @type {RfiCostImpact}
   * @memberof Rfi
   */
  costImpact?: RfiCostImpact;
  /**
   *
   * @type {RfiScheduleImpact}
   * @memberof Rfi
   */
  scheduleImpact?: RfiScheduleImpact;
  /**
   *
   * @type {RfiPriority}
   * @memberof Rfi
   */
  priority?: RfiPriority;
  /**
   *
   * @type {RfiDiscipline}
   * @memberof Rfi
   */
  discipline?: RfiDiscipline;
  /**
   *
   * @type {RfiCategory}
   * @memberof Rfi
   */
  category?: RfiCategory;
  /**
   * RFI subcontructor id
   * @type {string}
   * @memberof Rfi
   */
  reference?: string;
  /**
   *
   * @type {any}
   * @memberof Rfi
   */
  sheetMetadata?: SheetMetadata; // BIM only
  /**
   *
   * @type {RfiBasePushpinAttributes}
   * @memberof Rfi
   */
  pushpinAttributes?: RfiBasePushpinAttributes; // BIM only

  dueDate?: string;

  // Build only
  virtualFolderUrn?: string;
}

/**
 *
 * @export
 * @interface Rfi
 */
export interface Rfi extends BaseRfi {
  /**
   *
   * @type {RfiPermittedActions}
   * @memberof Rfi
   */
  permittedActions: RfiPermittedActions;
}

/**
 * The custom identifier of the RFI (required in case that status is set to open).
 * @export
 * @type RfiCustomIdentifier
 */
export type RfiCustomIdentifier = string;

/**
 *
 * @export
 * @interface RfiLocation
 */
export interface RfiLocation {
  /**
   * A description of the location of the RFI in the construction project.
   * @type {string}
   * @memberof RfiLocation
   */
  description: string;
}

export interface SheetMetadata {
  // BIM only
  is3D: boolean;
  sheetGuid: string;
  sheetName: string;
}

/**
 * @export
 * @enum {string}
 */
export enum AssignedToType {
  User = 'user',
}

/**
 * Pushpin data
 * @export
 * @interface RfiBasePushpinAttributes
 */
export interface RfiBasePushpinAttributes {
  /**
   * Location coordinates data
   * @type {any}
   * @memberof RfiBasePushpinAttributes
   */
  location?: any;
  /**
   * Pushpin type
   * @type {string}
   * @memberof RfiBasePushpinAttributes
   */
  type?: PushpinAttributesType;
  /**
   * timestamp when pushpin created
   * @type {Date}
   * @memberof RfiBasePushpinAttributes
   */
  createdAt: Date;
  /**
   * Autodesk ID of the user that created the pushpin
   * @type {string}
   * @memberof RfiBasePushpinAttributes
   */
  createdBy?: string;
  /**
   * doc version when RFI was created on
   * @type {number}
   * @memberof RfiBasePushpinAttributes
   */
  createdDocVersion?: number;
}

/**
 * The list of actions that are permitted for the user.
 * @export
 * @interface RfiPermittedActions
 */
export interface RfiPermittedActions {
  /**
   *
   * @type {RfiPermittedActionsUpdateRfi}
   * @memberof RfiPermittedActions
   */
  updateRfi?: RfiPermittedActionsUpdateRfi;
  /**
   * Whether current user can create a comment on the RFI
   * @type {boolean}
   * @memberof RfiPermittedActions
   */
  createComment?: boolean;
  /**
   * Whether current user can create an attachment on the RFI
   * @type {boolean}
   * @memberof RfiPermittedActions
   */
  createAttachment?: boolean;
  /**
   * Whether current user can link/unlink PCO on the RFI
   * @type {boolean}
   * @memberof RfiPermittedActions
   */
  linkPCO?: boolean;
  /**
   * Whether current user can hide the RFI
   * @type {boolean}
   * @memberof RfiPermittedActions
   */
  togglePushpinVisibility?: boolean;
}

/**
 * Update RFI action
 * @export
 * @interface RfiPermittedActionsUpdateRfi
 */
export interface RfiPermittedActionsUpdateRfi {
  /**
   * List of statuses in which the user can update the RFI
   * @type {Array<PermittedRfiStatus>}
   * @memberof RfiPermittedActionsUpdateRfi
   */
  permittedStatuses?: Array<PermittedRfiStatus>;
  /**
   * List of attributes the user can update on the RFI when not changing the status
   * @type {Array<PermittedRfiAttribute>}
   * @memberof RfiPermittedActionsUpdateRfi
   */
  permittedAttributes?: Array<PermittedRfiAttribute>;
}

/**
 * Permitted RFI status
 * @export
 * @interface PermittedRfiStatus
 */
export interface PermittedRfiStatus {
  /**
   *
   * @type {RfiStatus}
   * @memberof PermittedRfiStatus
   */
  status?: RfiStatus;
  /**
   * The list of attributes that are required when creating an RFI.
   * @type {Array<PermittedRfiAttribute>}
   * @memberof PermittedRfiStatus
   */
  requiredAttributes?: Array<PermittedRfiAttribute>;
}

/**
 * Permitted RFI Attribute
 * @export
 * @interface PermittedRfiAttribute
 */
export interface PermittedRfiAttribute {
  /**
   * The name of the RFI Attribute.
   * @type {string}
   * @memberof PermittedRfiAttribute
   */
  name: string;
  /**
   * The list of possible values for the attribute.
   * @type {Array<PermittedRfiAttributeValues>}
   * @memberof PermittedRfiAttribute
   */
  values?: Array<PermittedRfiAttributeValues>;
}
/**
 *
 * @export
 * @interface PermittedRfiAttributeValues
 */
export interface PermittedRfiAttributeValues {
  /**
   * The value of the RFI attribute.
   * @type {string}
   * @memberof PermittedRfiAttributeValues
   */
  value: string;
  /**
   * The type of RFI attribute.
   * @type {string}
   * @memberof PermittedRfiAttributeValues
   */
  type?: string;
}
